import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SharedCopayAssistanceRequestTimelineSchema } from '@libs/shared/domain/copay-assistance/request/timeline/shared-copay-assistance-request-timeline.schema';
import { DateService } from '@services/date.service';
import { LogService } from '@services/log.service';
import { UrlService } from '@services/url.service';
import { CursorPage } from '@shared/components/data-table/paginator';
import { ApiList } from '@shared/models/api-list.model';
import { CodeRequestResultType } from '@shared/models/code-request-result.enum';
import { CopayRequestSchema } from '@shared/models/copay-request-schema';
import { CopayRequestsLiveAggregates } from '@shared/models/copay/copay-requests-live-aggregates';
import { DynamodbFilterBuilderInputSchema } from '@shared/models/dynamodb/dynamodb-filter-builder-input.schema';
import { DynamodbFilterConditionSchema } from '@shared/models/dynamodb/dynamodb-filter-condition.schema';
import { DynamodbFilterValueSchema } from '@shared/models/dynamodb/dynamodb-filter-value.schema';
import { DynamodbMatchType } from '@shared/models/dynamodb/dynamodb-match-type.enum';
import { TenantIdType } from '@shared/models/tenant-id.type';
import { chain } from 'lodash';
import moment from 'moment-timezone';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CopayApiService {
  constructor(
    private http: HttpClient,
    private logService: LogService,
    private urlService: UrlService,
    private dateService: DateService,
  ) {}

  searchCodeRequests(
    search: Record<string, any>,
    filter: Record<string, DynamodbFilterValueSchema>,
    page?: CursorPage,
    includeOffset = true,
    tenantId?: TenantIdType,
  ): Observable<ApiList<CopayRequestSchema>> {
    let url = this.urlService.COPAY_URL;
    if (tenantId) {
      url = url.replace('{tenant}', tenantId);
    }
    const body = this.createBody(search, filter, page, includeOffset);
    return this.http.post<ApiList<CopayRequestSchema>>(url, body).pipe(map(copayRequests => this.fixStatusValue(copayRequests)));
  }

  getProgramCounts(search: Record<string, any>, filter: Record<string, any>): Observable<Record<string, number>> {
    const url = this.urlService.COPAY_PROGRAMS_COUNT_URL;
    const body = this.createBody(search, filter);
    return this.http.post<Record<string, number>>(url, body);
  }

  getTimeline(copayRequest: CopayRequestSchema): Observable<SharedCopayAssistanceRequestTimelineSchema> {
    const url = chain(this.urlService.COPAY_TIMELINE_URL)
      .replace('{ncpdp}', copayRequest.ncpdp)
      .replace('{created}', this.dateService.toISOStringFixedMs(copayRequest.created))
      .value();

    return this.http.get<SharedCopayAssistanceRequestTimelineSchema>(url).pipe(catchError(error => this.handleTimelineError(error)));
  }

  getLiveAggregates(search: Record<string, any>, filter: Record<string, any> = {}): Observable<CopayRequestsLiveAggregates> {
    const url = this.urlService.COPAY_LIVE_AGGREGATES_URL;
    const body = this.createBody(search, filter, undefined, false);
    return this.http.post<CopayRequestsLiveAggregates>(url, body);
  }

  getResultOptions() {
    return [
      CodeRequestResultType.Incomplete,
      CodeRequestResultType.Consignment,
      CodeRequestResultType.InProgress,
      CodeRequestResultType.Paid,
      CodeRequestResultType.PaidEligible,
      CodeRequestResultType.Rejected,
      CodeRequestResultType.Reversed,
      CodeRequestResultType.PaApproved,
      CodeRequestResultType.PaPending,
      CodeRequestResultType.PaReady,
    ];
  }

  private fixStatusValue(copayRequests: ApiList<CopayRequestSchema>) {
    copayRequests.items.forEach(item => {
      if (this.isCopayRequestComplete(item) && this.isCopayRequestInProgress(item)) {
        item.result = '';
      }
    });
    return copayRequests;
  }

  private isCopayRequestComplete(copayRequest: CopayRequestSchema): boolean {
    return !!copayRequest.end;
  }

  private isCopayRequestInProgress(copayRequest: CopayRequestSchema): boolean {
    return copayRequest.result === 'In Progress';
  }

  private createBody(
    search: Record<string, any>,
    filterValues: Record<string, DynamodbFilterValueSchema>,
    page?: CursorPage,
    includeOffset = true,
  ) {
    const body = {
      createdFrom: search.createdFrom,
      createdTo: search.createdTo,
      filledDateFrom: search.filledDateFrom,
      filledDateTo: search.filledDateTo,
      filter: this.createFilter(filterValues),
      result: search.result,
    } as any;

    if (page) {
      body.page = page;
    }

    if (includeOffset) {
      body.offsetFromUtc = moment().utcOffset();
    }

    if (search.programs) {
      body.programs = search.programs;
    } else if (search.ncpdp) {
      body.ncpdp = search.ncpdp;
    } else if (search.consignmentPharmacyNpi) {
      body.consignmentPharmacyNpi = search.consignmentPharmacyNpi;
    } else if (search.tenant) {
      body.tenant = search.tenant;
    }

    if (search.sort) {
      body.sort = search.sort;
    }

    if (search.reverse) {
      body.reverse = search.reverse;
    }

    return body;
  }

  private createFilter(filterValues: Record<string, DynamodbFilterValueSchema>) {
    const filter: DynamodbFilterBuilderInputSchema = { conditions: [] };
    for (const [attributeName, filterValue] of Object.entries(filterValues)) {
      if (filterValues[attributeName].value != null || this.matchTypeDoesntNeedValue(filterValues[attributeName].matchType)) {
        if (Array.isArray(filterValue.value) && !filterValue.value.length) {
          continue;
        }
        this.addExpression(attributeName, filterValue, filter);
      }
    }
    return filter;
  }

  private matchTypeDoesntNeedValue(matchType: DynamodbMatchType): unknown {
    return matchType === DynamodbMatchType.Exists || matchType === DynamodbMatchType.NotExists;
  }

  private addExpression(attributeName: string, filterValues: DynamodbFilterValueSchema, filter: DynamodbFilterBuilderInputSchema) {
    let condition = {} as DynamodbFilterConditionSchema;
    if (filterValues.matchType === DynamodbMatchType.Condition) {
      condition = filterValues.value as any;
    } else {
      if (filterValues.matchType === DynamodbMatchType.In && Array.isArray(filterValues.value)) {
        condition.values = this.createValuesForArrayAttribute(attributeName, filterValues.value);
        condition.expression = this.createExpressionForArrayAttribute(attributeName, condition.values);
        condition.name = `cond_${attributeName}`;
      } else if (filterValues.matchType === DynamodbMatchType.BeginsWith) {
        condition.expression = `begins_with({${attributeName}}, [${attributeName}])`;
        condition.values = { [attributeName]: filterValues.value };
        condition.name = `${attributeName}`;
      } else if (filterValues.matchType === DynamodbMatchType.Exists) {
        condition.expression = `attribute_exists({${attributeName}})`;
        condition.name = `exists_${attributeName}`;
      } else if (filterValues.matchType === DynamodbMatchType.NotExists) {
        condition.expression = `attribute_not_exists(${attributeName})`;
        condition.name = `not_exists_${attributeName}`;
      } else if (filterValues.matchType === DynamodbMatchType.NotEquals) {
        condition.expression = `{${attributeName}} <> [${attributeName}]`;
        condition.values = { [attributeName]: filterValues.value };
        condition.name = `cond_${attributeName}`;
      } else {
        condition.expression = `{${attributeName}} = [${attributeName}]`;
        condition.values = { [attributeName]: filterValues.value };
        condition.name = `cond_${attributeName}`;
      }
    }
    if (!filter.expression) {
      filter.expression = `{${condition.name}}`;
    } else {
      filter.expression += ` AND {${condition.name}}`;
    }

    filter.conditions.push(condition);
  }

  private createValuesForArrayAttribute(attributeName: string, attributeValue: unknown[]): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    attributeValue.forEach((value, index) => {
      values[attributeName + index] = value;
    });
    return values;
  }

  private createExpressionForArrayAttribute(attributeName: string, values: Record<string, unknown>): string {
    const operands = Object.keys(values).map(key => `[${key}]`);

    return `{${attributeName}} IN (${operands.join(', ')})`;
  }

  private handleTimelineError(error: HttpErrorResponse) {
    if (error.status !== 404) {
      this.logService.error(error);
    }
    return of({ steps: [] });
  }
}
